import React, { useState } from 'react';
import * as mui from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { SectionHeader, formatDate } from './UIComponents';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import RefreshIcon from '@mui/icons-material/Refresh';
import SecurityIcon from '@mui/icons-material/Security';
import TuneIcon from '@mui/icons-material/Tune';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const TerminatedUserAnalysis = ({ 
  domains, 
  terminatedUsers,
  setTerminatedUsers,
  fetchAnalysis,
  showNotification 
}) => {
  const [analysisFilter, setAnalysisFilter] = useState({
    days: 30,
    domain: ''
  });
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);

  const handleAnalysisFilterChange = (e) => {
    const { name, value } = e.target;
    setAnalysisFilter({ ...analysisFilter, [name]: value });
  };

  const runAnalysis = async () => {
    setLoadingAnalysis(true);
    try {
      const results = await fetchAnalysis(analysisFilter);
      setTerminatedUsers(results);
      if (results.length === 0) {
        showNotification('Analysis Complete', 'No terminated users with active network access were found.');
      } else if (results.some(user => user.RISK_LEVEL === 'HIGH')) {
        showNotification('High Risk Detected', 'Some terminated users still have active network access with high risk level.');
      } else {
        showNotification('Analysis Complete', `${results.length} terminated users with active network access were found.`);
      }
    } catch (error) {
      console.error('Error running analysis:', error);
      showNotification('Error', error.message || 'An error occurred during analysis');
    } finally {
      setLoadingAnalysis(false);
    }
  };

  // Terminated user columns for DataGrid
  const terminatedUserColumns = [
    { field: 'EMAIL', headerName: 'Email', flex: 1 },
    { field: 'USERNAME', headerName: 'Username', flex: 1 },
    { 
      field: 'TERMINATION_DATE', 
      headerName: 'Termination Date', 
      width: 180,
      valueFormatter: (params) => params && params.value ? formatDate(params.value) : 'N/A'
    },
    { 
      field: 'LAST_LOGIN', 
      headerName: 'Last Login', 
      width: 180,
      valueFormatter: (params) => params && params.value ? formatDate(params.value) : 'Never'
    },
    { 
      field: 'DAYS_SINCE_TERMINATION', 
      headerName: 'Days Since Termination', 
      width: 180,
      valueFormatter: (params) => params && params.value ? `${params.value} days` : 'N/A'
    },
    { 
      field: 'RISK_LEVEL', 
      headerName: 'Risk Level', 
      width: 120,
      renderCell: (params) => {
        const riskLevel = params.row.RISK_LEVEL || 'LOW';
        let color = 'success';
        if (riskLevel === 'MEDIUM') color = 'warning';
        if (riskLevel === 'HIGH') color = 'error';
        
        return (
          <mui.Chip 
            label={riskLevel} 
            color={color} 
            size="small" 
          />
        );
      }
    },
  ];

  return (
    <mui.Paper sx={{ p: 3, mb: 4 }}>
      <SectionHeader 
        title="Terminated User Analysis" 
        icon={<VerifiedUserIcon />} 
        description="Analyze terminated users with active network access" 
      />
      
      <mui.Box sx={{ mt: 3, mb: 4, p: 3, bgcolor: 'background.default', borderRadius: 2 }}>
        <mui.Typography variant="h6" gutterBottom>
          Analysis Filters
        </mui.Typography>
        
        <mui.Grid container spacing={3} alignItems="center">
          <mui.Grid item xs={12} md={4}>
            <mui.FormControl fullWidth margin="normal">
              <mui.InputLabel id="analysis-days-label">Time Period</mui.InputLabel>
              <mui.Select
                labelId="analysis-days-label"
                id="analysis-days"
                name="days"
                value={analysisFilter.days}
                label="Time Period"
                onChange={handleAnalysisFilterChange}
              >
                <mui.MenuItem value={7}>Last 7 days</mui.MenuItem>
                <mui.MenuItem value={30}>Last 30 days</mui.MenuItem>
                <mui.MenuItem value={90}>Last 90 days</mui.MenuItem>
                <mui.MenuItem value={180}>Last 180 days</mui.MenuItem>
                <mui.MenuItem value={365}>Last 365 days</mui.MenuItem>
              </mui.Select>
              <mui.FormHelperText>Time period for analysis</mui.FormHelperText>
            </mui.FormControl>
          </mui.Grid>
          <mui.Grid item xs={12} md={4}>
            <mui.FormControl fullWidth margin="normal">
              <mui.InputLabel id="analysis-domain-label">Domain</mui.InputLabel>
              <mui.Select
                labelId="analysis-domain-label"
                id="analysis-domain"
                name="domain"
                value={analysisFilter.domain}
                label="Domain"
                onChange={handleAnalysisFilterChange}
              >
                <mui.MenuItem value="">All Domains</mui.MenuItem>
                {domains.map((domain) => (
                  <mui.MenuItem key={domain.id} value={domain.id}>
                    {domain.DOMAIN_NAME}
                  </mui.MenuItem>
                ))}
              </mui.Select>
              <mui.FormHelperText>Filter by specific domain</mui.FormHelperText>
            </mui.FormControl>
          </mui.Grid>
          <mui.Grid item xs={12} md={4}>
            <mui.Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <mui.Button 
                variant="contained" 
                color="primary" 
                onClick={runAnalysis}
                startIcon={<RefreshIcon />}
                size="large"
                disabled={loadingAnalysis}
              >
                {loadingAnalysis ? 'Running Analysis...' : 'Run Analysis'}
              </mui.Button>
            </mui.Box>
          </mui.Grid>
        </mui.Grid>
      </mui.Box>
      
      {loadingAnalysis ? (
        <mui.Box sx={{ display: 'flex', justifyContent: 'center', my: 4, height: 400 }}>
          <mui.CircularProgress />
        </mui.Box>
      ) : terminatedUsers.length > 0 ? (
        <>
          {terminatedUsers.some(user => user.RISK_LEVEL === 'HIGH') && (
            <mui.Alert severity="error" sx={{ mb: 3 }}>
              <mui.AlertTitle>High Risk Detected</mui.AlertTitle>
              <mui.Typography variant="body2">
                {terminatedUsers.filter(u => u.RISK_LEVEL === 'HIGH').length} 
                {' '}terminated users still have active network access with high risk level.
              </mui.Typography>
            </mui.Alert>
          )}
          
          <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <mui.Typography variant="subtitle1">
              {terminatedUsers.length} terminated users found
            </mui.Typography>
            <mui.Button 
              variant="outlined" 
              size="small" 
              startIcon={<FileDownloadIcon />}
            >
              Export Results
            </mui.Button>
          </mui.Box>
          
          <mui.Box sx={{ height: 500, width: '100%' }}>
            <DataGrid
              rows={terminatedUsers}
              columns={terminatedUserColumns}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              disableSelectionOnClick
              getRowId={(row, index) => row.id || index}
            />
          </mui.Box>
        </>
      ) : (
        <mui.Box sx={{ p: 3, textAlign: 'center', height: 400, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
          <SecurityIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
          <mui.Typography variant="h6" gutterBottom>
            No Terminated Users Found
          </mui.Typography>
          <mui.Typography variant="body2" color="text.secondary" sx={{ maxWidth: 500, mb: 3 }}>
            No terminated users with active network access were found based on the current filters.
          </mui.Typography>
          <mui.Button 
            variant="outlined" 
            startIcon={<TuneIcon />}
            onClick={() => runAnalysis()}
          >
            Adjust Filters
          </mui.Button>
        </mui.Box>
      )}
    </mui.Paper>
  );
};

export default TerminatedUserAnalysis; 